import { useEffect } from "react";

/**
 * Trava a rolagem do `<body>` enquanto `locked` for verdadeiro e restaura o
 * valor anterior ao destravar/desmontar.
 *
 * Extraído das cópias que existiam em `VideoModal` e `MarketingHeader` — ambas
 * migradas para cá, junto com o `Modal` base (e portanto todos os modais que o
 * reusam). A rolagem de fundo "roubando" o scroll do usuário é o bug clássico
 * de modal alto, e a correção precisa morar num único lugar: a cópia do
 * `MarketingHeader` escrevia `overflow = ''` sem guardar o valor anterior, ou
 * seja, destravava a página por baixo de um modal aberto ao mesmo tempo.
 *
 * ## Por que `overflow: hidden` e não `position: fixed`
 *
 * `position: fixed` no body tira o documento do fluxo, o navegador zera o
 * `scrollY` e o usuário "pula" para o topo ao fechar o modal — só dá para
 * disfarçar guardando e reaplicando `window.scrollTo` manualmente.
 * `overflow: hidden` NÃO altera a posição de rolagem: o documento continua
 * onde estava, apenas não rola. Sem pulo, sem código de compensação.
 *
 * ## Aninhamento
 *
 * Não há contador de travas. O empilhamento funciona **desde que o desmonte
 * seja LIFO** (o último a travar é o primeiro a destravar): o interno guarda
 * `"hidden"` — o valor deixado pelo externo — e o devolve ao fechar; o externo
 * restaura o valor original depois. Fora de LIFO a garantia cai: se dois
 * modais irmãos estiverem abertos e o externo fechar primeiro, ele restaura o
 * valor original com o interno ainda aberto e a página destrava. Se esse
 * cenário aparecer, troque o corpo do hook por um contador global de travas.
 *
 * ⚠️ No iOS Safari `overflow: hidden` no body não bloqueia 100% o
 * "rubber banding". Combine com `overscroll-behavior: contain` no elemento
 * rolável interno (é o que os modais do projeto fazem).
 *
 * @param {boolean} [locked=true] — trava enquanto verdadeiro.
 */
export default function useScrollLock(locked = true) {
  useEffect(() => {
    if (!locked) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [locked]);
}
